import React from 'react';

import store from '@store/appStore';

import ApplicationErrorFallback from './ApplicationErrorFallback';
import { isBenignUnhandledRejection, isIgnorableWindowErrorEvent } from './globalWindowErrorUtils';

/**
 * SSAP fatal error surface. Mounted in `MemberPortalRoutes.tsx` around `SsapContainer` routes.
 *
 * Intentionally separate from `SsapSummaryErrorBoundary` (Final Review / comparison only): that inner
 * boundary runs first there and shows SSAP-specific recovery (`SsapSummaryFallback`); everything else
 * bubbles here for a generic reload fallback plus global `window` handlers.
 *
 * Covers:
 * - React: render, lifecycle, child constructors (error boundary).
 * - Window "error": uncaught exceptions (e.g. click handlers) except ignorable noise (resource loads, cross-origin Script error., …).
 * - "unhandledrejection": unhandled promise rejections.
 *
 * Does not replace console logging; developers still see failures in DevTools.
 */
class ApplicationErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.onWindowError = this.onWindowError.bind(this);
        this.onUnhandledRejection = this.onUnhandledRejection.bind(this);
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidMount() {
        window.addEventListener('error', this.onWindowError);
        window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    }

    componentWillUnmount() {
        window.removeEventListener('error', this.onWindowError);
        window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
    }

    onWindowError(event) {
        if (this.state.hasError || isIgnorableWindowErrorEvent(event)) {
            return;
        }
        if (!global.__ENV__.PRODUCTION) {
            console.error('[ApplicationErrorBoundary] window error', event.error || event.message);
        }
        this.setState({ hasError: true });
    }

    onUnhandledRejection(event) {
        if (this.state.hasError || isBenignUnhandledRejection(event)) {
            return;
        }
        if (!global.__ENV__.PRODUCTION) {
            console.error('[ApplicationErrorBoundary] unhandledrejection', event.reason);
        }
        this.setState({ hasError: true });
    }

    componentDidCatch(error, errorInfo) {
        if (!global.__ENV__.PRODUCTION) {
            console.error('[ApplicationErrorBoundary]', error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            const getState = this.props.getState || (() => store.getState());

            return (
                <ApplicationErrorFallback
                    getState={getState}
                    onReload={() => window.location.reload()}
                />
            );
        }

        return this.props.children;
    }
}

export default ApplicationErrorBoundary;
